import { useEffect, useMemo } from "react";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { assetUrl } from "@/lib/api";

// Cylindrical/wave curve engine lives below (curveGeom). Flat shapes/logos/enamel wrap
// onto a surface; the bend is computed in WORLD-scaled space so thin pieces still curve
// visibly instead of only growing/shrinking.
const asScale3 = (s) => (Array.isArray(s) ? s : [s || 1, s || 1, s || 1]);

// Resolve independent per-axis bends (bendX/bendY). Backward-compatible with the old
// single `bend` + `bendAxis` scheme.
function resolveBends(part) {
  const legacyX = (part.bendAxis || "x") !== "y" ? (part.bend || 0) : 0;
  const legacyY = (part.bendAxis || "x") === "y" ? (part.bend || 0) : 0;
  return [part.bendX ?? legacyX, part.bendY ?? legacyY];
}

// Generalized curve: deforms the geometry using `varAxis` as the running coordinate and
// `depthAxis` as the direction it curves toward. `curves`=1 → a single smooth cylindrical
// arc (used to wrap a flat logo onto a surface). `curves`>=2 → a SMOOTH, CONTINUOUS sine
// wave with that many gentle undulations: the logo keeps its pattern intact and simply
// rides the wave (e.g. "2026" rises while "POLITECNICA" dips) with no cuts or hard corners.
function curveGeom(g, amount, varAxis, depthAxis, curves, scale, bounds) {
  if (!amount) return;
  curves = Math.max(1, Math.round(curves || 1));
  const sMap = { x: scale[0] || 1, y: scale[1] || 1, z: scale[2] || 1 };
  const sVar = sMap[varAxis], sDep = sMap[depthAxis];
  let minV, maxV;
  if (bounds) { minV = bounds.min; maxV = bounds.max; }
  else {
    g.computeBoundingBox();
    const bb = g.boundingBox;
    minV = (varAxis === "x" ? bb.min.x : varAxis === "y" ? bb.min.y : bb.min.z) * sVar;
    maxV = (varAxis === "x" ? bb.max.x : varAxis === "y" ? bb.max.y : bb.max.z) * sVar;
  }
  const totalW = (maxV - minV) || 1;
  const pos = g.attributes.position;
  const getA = (i) => (varAxis === "x" ? pos.getX(i) : varAxis === "y" ? pos.getY(i) : pos.getZ(i)) * sVar;
  const getD = (i) => (depthAxis === "x" ? pos.getX(i) : depthAxis === "y" ? pos.getY(i) : pos.getZ(i)) * sDep;
  const setA = (i, v) => (varAxis === "x" ? pos.setX(i, v) : varAxis === "y" ? pos.setY(i, v) : pos.setZ(i, v));
  const setD = (i, v) => (depthAxis === "x" ? pos.setX(i, v) : depthAxis === "y" ? pos.setY(i, v) : pos.setZ(i, v));

  if (curves <= 1) {
    // Smooth single cylindrical arc (wrap).
    const center = (minV + maxV) / 2;
    const angle = amount * Math.PI;
    if (!angle) return;
    const R = totalW / angle;
    for (let i = 0; i < pos.count; i++) {
      const a = getA(i), d = getD(i);
      const theta = (a - center) / R;
      const radial = R - d;
      setA(i, (center + radial * Math.sin(theta)) / sVar);
      setD(i, (R - radial * Math.cos(theta)) / sDep);
    }
  } else {
    // Smooth continuous sine undulation: `curves` full periods across the span. The vertex
    // only shifts along the depth axis by a smooth amount, so text/shapes stay unbroken.
    const amp = amount * totalW * 0.12; // gentle amplitude, tuned by the slider
    for (let i = 0; i < pos.count; i++) {
      const a = getA(i), d = getD(i);
      const phase = ((a - minV) / totalW) * Math.PI * 2 * curves;
      setD(i, (d + amp * Math.sin(phase)) / sDep);
    }
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
}

// Per-axis curve amounts + wave counts. X/Y curve the plate out of plane (depth Z);
// Z curls the plate within its own plane (a banner/flag curving left-right).
function resolveCurves(part) {
  const [bx, by] = resolveBends(part);
  return {
    x: { amt: bx, curves: part.curvesX ?? 1 },
    y: { amt: by, curves: part.curvesY ?? 1 },
    z: { amt: part.bendZ ?? 0, curves: part.curvesZ ?? 1 },
  };
}

function hasCurves(part) {
  const c = resolveCurves(part);
  return !!(c.x.amt || c.y.amt || c.z.amt);
}

function applyCurves(g, part, scale, bnds) {
  const c = resolveCurves(part);
  if (c.x.amt) curveGeom(g, c.x.amt, "x", "z", c.x.curves, scale, bnds?.x);
  if (c.y.amt) curveGeom(g, c.y.amt, "y", "z", c.y.curves, scale, bnds?.y);
  if (c.z.amt) curveGeom(g, c.z.amt, "x", "y", c.z.curves, scale, bnds?.x); // in-plane curl around Z
}

function applyCurvesRoot(root, part, scale) {
  // Curve every mesh of a multi-mesh logo as ONE piece: use a SHARED bounding box (in the
  // root's canonical, baked frame) so all meshes wrap around the same center instead of each
  // curving around its own — otherwise multi-part logos split apart.
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  const sx = scale[0] || 1, sy = scale[1] || 1;
  const bnds = {
    x: { min: box.min.x * sx, max: box.max.x * sx },
    y: { min: box.min.y * sy, max: box.max.y * sy },
  };
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    o.geometry = o.geometry.clone();
    applyCurves(o.geometry, part, scale, bnds);
  });
}

// Build a 2D girdle outline (array of [x,z], centered, footprint ~[-0.5,0.5]) for each
// classic jewelry cut. Used to construct a faceted gem (crown + table + pavilion).
function gemOutline(cut) {
  const ell = (rx, rz, seg) => { const p = []; for (let i = 0; i < seg; i++) { const a = (i / seg) * Math.PI * 2; p.push([Math.cos(a) * rx, Math.sin(a) * rz]); } return p; };
  const rrect = (rx, rz, r, cseg) => {
    const p = []; const cx = rx - r, cz = rz - r;
    const arcs = [[cx, cz, 0], [-cx, cz, Math.PI / 2], [-cx, -cz, Math.PI], [cx, -cz, Math.PI * 1.5]];
    for (const [ax, az, base] of arcs) for (let i = 0; i <= cseg; i++) { const a = base + (Math.PI / 2) * (i / cseg); p.push([ax + Math.cos(a) * r, az + Math.sin(a) * r]); }
    return p;
  };
  const octa = (rx, rz, c) => [[rx - c, rz], [-(rx - c), rz], [-rx, rz - c], [-rx, -(rz - c)], [-(rx - c), -rz], [rx - c, -rz], [rx, -(rz - c)], [rx, rz - c]];
  const marquise = () => { const p = []; const seg = 16, A = 0.5, B = 0.32, pw = 0.85; for (let i = 0; i <= seg; i++) { const u = -1 + 2 * i / seg; p.push([u * A, B * Math.pow(Math.max(0, 1 - u * u), pw)]); } for (let i = seg - 1; i >= 1; i--) { const u = -1 + 2 * i / seg; p.push([u * A, -B * Math.pow(Math.max(0, 1 - u * u), pw)]); } return p; };
  const pear = () => { const p = []; const R = 0.33, cz = -0.10, tip = 0.52; const start = Math.PI * 0.30, end = Math.PI * 2 - Math.PI * 0.30, seg = 20; for (let i = 0; i <= seg; i++) { const a = start + (end - start) * i / seg; p.push([Math.sin(a) * R, cz + Math.cos(a) * R]); } p.push([0, tip]); return p; };
  switch (cut) {
    case "oval": return ell(0.5, 0.36, 18);
    case "cushion": return rrect(0.48, 0.48, 0.17, 4);
    case "elongated_cushion": return rrect(0.5, 0.32, 0.12, 4);
    case "princess": return [[0.5, 0.5], [-0.5, 0.5], [-0.5, -0.5], [0.5, -0.5]];
    case "emerald": return octa(0.5, 0.36, 0.14);
    case "asscher": return octa(0.46, 0.46, 0.15);
    case "radiant": return octa(0.5, 0.4, 0.12);
    case "marquise": return marquise();
    case "pear": return pear();
    case "round":
    default: return ell(0.5, 0.5, 16);
  }
}

// Solid faceted gem: crown facets (girdle -> inset table), a flat table on top, and
// pavilion facets converging to a culet point below. Flat, wide proportions read as a
// real cut stone; rendered double-sided so it looks solid (not a hollow shell).
function buildGemGeometry(cut) {
  const outline = gemOutline(cut || "round");
  const n = outline.length;
  const tableY = 0.26, tableScale = 0.5, culetY = -0.42;
  const pos = [];
  const T = (a, b, c) => { pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]); };
  const g = outline.map((p) => [p[0], 0, p[1]]);
  const tr = outline.map((p) => [p[0] * tableScale, tableY, p[1] * tableScale]);
  const tc = [0, tableY, 0], cu = [0, culetY, 0];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    T(tr[i], g[i], g[j]); T(tr[i], g[j], tr[j]);    // crown band (outward winding)
    T(tc, tr[i], tr[j]);                             // table
    T(cu, g[j], g[i]);                               // pavilion
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return geo;
}

// Build a primitive geometry with shape modifiers (taper -> trapezoid/pyramid,
// shear -> parallelogram) plus optional bend, so cubes/enamel can be reshaped.
function buildPrimitiveGeometry(part) {
  const d = part.dims || {};
  const kind = part.primitive;
  let g;
  switch (kind) {
    case "box": g = new THREE.BoxGeometry(1, 1, 1, 32, 32, 8); break;
    case "sphere": g = new THREE.SphereGeometry(0.6, 48, 48); break;
    case "cylinder": g = new THREE.CylinderGeometry(d.radius ?? 0.5, d.radius ?? 0.5, d.height ?? 1, 48); break;
    case "cone": g = new THREE.ConeGeometry(d.radius ?? 0.5, d.height ?? 1, 48); break;
    case "pyramid": g = new THREE.ConeGeometry(d.radius ?? 0.6, d.height ?? 1, 4); break;
    case "prism": g = new THREE.CylinderGeometry(d.radiusTop ?? d.radius ?? 0.5, d.radiusBottom ?? d.radius ?? 0.5, d.height ?? 1, Math.max(3, Math.round(d.sides ?? 3))); break;
    case "torus": g = new THREE.TorusGeometry(d.radius ?? 0.5, d.tube ?? 0.18, 24, 96); break;
    case "gem": g = buildGemGeometry(d.cut); break;
    default: g = new THREE.BoxGeometry(1, 1, 1, 32, 32, 8);
  }
  const taperTop = part.taperTop ?? part.taper ?? 0;      // 0..1 afinar el ancho superior
  const taperBottom = part.taperBottom ?? 0;              // 0..1 afinar el ancho inferior
  const taperLeft = part.taperLeft ?? 0;                  // 0..1 afinar el lado izquierdo (−X)
  const taperRight = part.taperRight ?? 0;                // 0..1 afinar el lado derecho (+X)
  const shear = part.shear || 0;   // -1..1 inclinar (paralelogramo)
  if (taperTop || taperBottom || shear || taperLeft || taperRight) {
    g.computeBoundingBox();
    const bb = g.boundingBox;
    const minY = bb.min.y, hY = (bb.max.y - bb.min.y) || 1;
    const minX = bb.min.x, hX = (bb.max.x - bb.min.x) || 1;
    const pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      let x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const ty = (y - minY) / hY; // 0 abajo .. 1 arriba
      const tx = (x - minX) / hX; // 0 izq .. 1 der
      if (taperTop || taperBottom) {
        const f = (1 - taperBottom) * (1 - ty) + (1 - taperTop) * ty;
        x *= f; z *= f;
      }
      if (shear) x += shear * (ty - 0.5);
      if (taperLeft || taperRight) {
        const f = (1 - taperLeft) * (1 - tx) + (1 - taperRight) * tx;
        y *= f; z *= f;
      }
      pos.setXYZ(i, x, y, z);
    }
    pos.needsUpdate = true;
    g.computeVertexNormals();
  }
  applyCurves(g, part, asScale3(part.scale));
  return g;
}

function PrimitivePart({ part, colorOverride, metalnessOverride, roughnessOverride, opacity = 1 }) {
  const col = colorOverride || part.color || "#C0C0C0";
  const isGem = part.primitive === "gem";
  const geom = useMemo(() => buildPrimitiveGeometry(part), // eslint-disable-line
    [part.primitive, JSON.stringify(part.dims), part.taper, part.taperTop, part.taperBottom, part.taperLeft, part.taperRight, part.shear, part.bend, part.bendAxis, part.bendX, part.bendY, part.bendZ, part.curvesX, part.curvesY, part.curvesZ, JSON.stringify(part.scale)]);
  return (
    <mesh castShadow geometry={geom}>
      {isGem ? (
        <meshPhysicalMaterial
          color={col}
          metalness={0.0}
          roughness={0.15}
          clearcoat={1}
          clearcoatRoughness={0.06}
          ior={1.9}
          reflectivity={0.6}
          envMapIntensity={1.2}
          flatShading
          side={THREE.DoubleSide}
          transparent={opacity < 1}
          opacity={opacity}
        />
      ) : (
        <meshStandardMaterial
          color={col}
          metalness={metalnessOverride ?? part.metalness ?? 0.85}
          roughness={roughnessOverride ?? part.roughness ?? 0.3}
          envMapIntensity={1.0}
          transparent={opacity < 1}
          opacity={opacity}
        />
      )}
    </mesh>
  );
}

// Relief thickness for normalized logos is a WORLD value (default below), editable per
// zone template via `part.reliefDepth`, and independent of the logo's XY size so bigger
// logos don't fan out / protrude more.
const RELIEF_WORLD_DEPTH = 0.09;
// Absolute-size ("medida") mode: real-world length -> model world units. Calibrated so a typical
// 8mm logo (~0.24 world) matches the usual zone footprint; the admin can fine-tune values visually.
const WORLD_PER_MM = 0.03;
const UNIT_MM = { mm: 1, cm: 10, in: 25.4 };
const toWorldLen = (val, unit) => (Number(val) || 0) * (UNIT_MM[unit] || 1) * WORLD_PER_MM;

// Auto-orient a customer-uploaded relief plate: image-to-3D engines (Meshy / 3D AI Studio)
// often output the plate with its depth (thin) axis NOT on Z, so a side face ends up facing
// forward. For clearly-flat plates we rotate the thinnest axis onto +Z so the logo face reads
// forward. Solid (non-flat) models are left untouched.
function autoOrientPlate(inner) {
  inner.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(inner);
  const size = new THREE.Vector3(); box.getSize(size);
  const center = new THREE.Vector3(); box.getCenter(center);
  const dims = [size.x, size.y, size.z];
  const minA = Math.min(...dims), maxA = Math.max(...dims);
  if (maxA <= 1e-6 || minA >= 0.6 * maxA) return; // not a flat plate → leave as-is
  const thin = dims.indexOf(minA);
  const axisName = thin === 0 ? "x" : thin === 1 ? "y" : "z";
  // Detect which side of the thin axis holds the RELIEF (readable face): the raised, contoured
  // side carries far more vertices than the flat backing. We then point that side to +Z (front),
  // so text/number logos never read from their back/underside ("la parte de abajo como frente").
  let plus = 0, minus = 0;
  const v = new THREE.Vector3();
  inner.traverse((o) => {
    if (!o.isMesh || !o.geometry?.attributes?.position) return;
    const pos = o.geometry.attributes.position;
    const step = Math.max(1, Math.floor(pos.count / 4000));
    for (let i = 0; i < pos.count; i += step) {
      v.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld);
      const d = v[axisName] - center[axisName];
      if (d > 1e-6) plus++; else if (d < -1e-6) minus++;
    }
  });
  const reliefPlus = plus >= minus; // is the relief/front on the + side of the thin axis?
  if (thin === 0) inner.rotation.y = reliefPlus ? -Math.PI / 2 : Math.PI / 2;      // +X/-X relief → +Z
  else if (thin === 1) inner.rotation.x = reliefPlus ? Math.PI / 2 : -Math.PI / 2;  // +Y/-Y relief → +Z
  else if (!reliefPlus) inner.rotation.x = Math.PI;                                 // thin Z, relief on -Z → +Z
}

// Bake a trapezoid taper (top/bottom/left/right, 0..1) into a normalized relief logo's
// meshes so it can be fitted to non-rectangular zones (signet tops, etc.). Operates in the
// root's local space using the footprint size; thickness (Z) is left untouched.
function deformTaperRoot(root, part, size) {
  const tTop = part.taperTop ?? part.taper ?? 0;
  const tBot = part.taperBottom ?? 0;
  const tL = part.taperLeft ?? 0;
  const tR = part.taperRight ?? 0;
  if (!(tTop || tBot || tL || tR)) return;
  root.updateMatrixWorld(true);
  const sx = size.x || 1, sy = size.y || 1;
  const tmp = new THREE.Vector3();
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    o.geometry = o.geometry.clone();
    const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert().multiply(o.matrixWorld);
    const fromRoot = new THREE.Matrix4().copy(toRoot).invert();
    const pos = o.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      tmp.fromBufferAttribute(pos, i).applyMatrix4(toRoot);
      const ty = tmp.y / sy + 0.5;
      const tx = tmp.x / sx + 0.5;
      if (tTop || tBot) tmp.x *= (1 - tBot) * (1 - ty) + (1 - tTop) * ty;
      if (tL || tR) tmp.y *= (1 - tL) * (1 - tx) + (1 - tR) * tx;
      tmp.applyMatrix4(fromRoot);
      pos.setXYZ(i, tmp.x, tmp.y, tmp.z);
    }
    pos.needsUpdate = true;
    o.geometry.computeVertexNormals();
  });
}

function GltfPart({ part, colorOverride, metalnessOverride, roughnessOverride, opacity = 1 }) {
  const { scene } = useGLTF(assetUrl(part.model_url));
  const doNormalize = part.normalize != null ? !!part.normalize : part.role === "logo";
  const isLogo = part.role === "logo";
  const cloned = useMemo(() => {
    const inner = scene.clone(true);
    const c = new THREE.Group();
    c.add(inner);
    // Per-logo MANUAL orientation offset (radians, 90°/180° steps) baked before normalizing —
    // fixes assets that come mis-oriented from Meshy/upload. Stored on the logo, so it applies
    // in every zone consistently. (90° steps => non-uniform depth scale stays shear-free.)
    const o = part.orient;
    if (Array.isArray(o) && (o[0] || o[1] || o[2])) inner.rotation.set(o[0], o[1], o[2]);
    else if (part.autoOrient || (doNormalize && part.autoOrient !== false)) autoOrientPlate(inner);
    c.updateMatrixWorld(true);
    let fit = [1, 1, 1];
    const s = asScale3(part.scale);
    if (doNormalize) {
      // Footprint (XY, aspecto) -> 1; grosor -> valor world (part.reliefDepth o default),
      // independiente del tamaño XY, para que todo logo comparta tamaño+grosor de la zona.
      const box = new THREE.Box3().setFromObject(c);
      const size = new THREE.Vector3(); box.getSize(size);
      const ctr = new THREE.Vector3(); box.getCenter(ctr);
      inner.position.sub(ctr);
      // Canonicalize: BAKE the orientation (autoOrient/orient) + recenter into the geometry so
      // that from here on X=width, Y=height, Z=front and the center is (0,0,0). The curve engine
      // and taper then operate in this single consistent frame — fixing "la curva en los ejes",
      // where curving used the raw GLB axes that didn't match the displayed orientation.
      c.updateMatrixWorld(true);
      const cInv = new THREE.Matrix4().copy(c.matrixWorld).invert();
      const meshes = [];
      c.traverse((o) => { if (o.isMesh && o.geometry) meshes.push(o); });
      meshes.forEach((m) => {
        const g = m.geometry.clone();
        g.applyMatrix4(new THREE.Matrix4().copy(cInv).multiply(m.matrixWorld));
        m.geometry = g;
        m.position.set(0, 0, 0); m.quaternion.identity(); m.scale.set(1, 1, 1);
        c.add(m); // reparent under c with identity local transform
      });
      c.remove(inner);
      deformTaperRoot(c, part, size);
      const depth = part.reliefDepth ?? RELIEF_WORLD_DEPTH;
      const fz = (depth / (Math.abs(s[2]) || 1)) / Math.max(size.z, 1e-6);
      if ((part.sizeMode || "ratio") === "abs") {
        // Medida exacta: el logo sale al ancho/alto/grosor reales (mm/cm/pulgadas), sin depender
        // de la escala de la zona (dividimos por `s` para cancelarla).
        const unit = part.sizeUnit || "mm";
        const wW = toWorldLen(part.absW ?? 8, unit);
        const wH = toWorldLen(part.absH ?? 8, unit);
        const dW = part.absD != null ? toWorldLen(part.absD, unit) : depth;
        fit = [(wW / (Math.abs(s[0]) || 1)) / Math.max(size.x, 1e-6),
               (wH / (Math.abs(s[1]) || 1)) / Math.max(size.y, 1e-6),
               (dW / (Math.abs(s[2]) || 1)) / Math.max(size.z, 1e-6)];
      } else {
        // Ratio (llena la zona): elige qué dimensión manda el llenado para que un logo largo no
        // se vea gigante, con multiplicadores de ancho/alto opcionales.
        const fitAxis = part.fitAxis || "longest";
        const base = fitAxis === "width" ? (1 / Math.max(size.x, 1e-6))
                   : fitAxis === "height" ? (1 / Math.max(size.y, 1e-6))
                   : (1 / Math.max(size.x, size.y, 1e-6));
        fit = [base * (part.ratioW ?? 1), base * (part.ratioH ?? 1), fz];
      }
    } else if (part.centered) {
      const box = new THREE.Box3().setFromObject(c);
      const ctr = new THREE.Vector3(); box.getCenter(ctr);
      inner.position.sub(ctr);
    }
    if (hasCurves(part)) {
      const eff = [s[0] * fit[0], s[1] * fit[1], s[2] * fit[2]];
      applyCurvesRoot(c, part, eff);
    }
    // Recompute normals for logos: meshopt/quantization can corrupt normals so the
    // metallic material renders black. Regenerating from the (preserved) positions fixes it.
    if (isLogo) {
      c.traverse((o) => {
        if (o.isMesh && o.geometry) {
          o.geometry = o.geometry.clone();
          o.geometry.computeVertexNormals();
        }
      });
    }
    c.scale.set(fit[0], fit[1], fit[2]);
    return c;
  }, [scene, part.bend, part.bendAxis, part.bendX, part.bendY, part.bendZ, part.curvesX, part.curvesY, part.curvesZ, part.centered, doNormalize, part.reliefDepth, part.taperTop, part.taperBottom, part.taperLeft, part.taperRight, JSON.stringify(part.orient), part.autoOrient, JSON.stringify(part.scale)]);
  useEffect(() => {
    const col = colorOverride || part.color || "#C0C0C0";
    cloned.traverse((o) => {
      if (o.isMesh && o.material) {
        o.material = o.material.clone();
        o.material.color = new THREE.Color(col);
        // Logos lost their beveled normals during meshopt compression; recomputed flat
        // normals turn a highly-metallic surface into a black mirror (reflects the dark
        // environment). Use a satin metal (lower metalness, higher roughness) so logos
        // always show the METAL COLOR via diffuse lighting instead of rendering black.
        const dMetal = isLogo ? 0.5 : 0.85;
        const dRough = isLogo ? 0.4 : 0.3;
        const effMetal = metalnessOverride ?? part.metalness ?? dMetal;
        const effRough = roughnessOverride ?? part.roughness ?? dRough;
        o.material.metalness = effMetal;
        o.material.roughness = effRough;
        o.material.envMapIntensity = isLogo ? 1.5 : 1.0;
        // Flat (compression-flattened) logo faces receive little light and read dark; a
        // subtle emissive floor in the metal color keeps metallic logos looking bright.
        // Non-metal materials (plastic/wood/fabric) must stay matte, so skip the floor.
        if (isLogo && effMetal >= 0.3) o.material.emissive = new THREE.Color(col).multiplyScalar(0.28);
        else o.material.emissive = new THREE.Color(0x000000);
        o.material.map = null;
        o.material.vertexColors = false;
        o.material.flatShading = false;
        o.material.side = THREE.DoubleSide;
        // Logos sit on top of the enamel/band surface: pull them toward the camera in the
        // depth buffer so they always render above coplanar surfaces (no z-fighting / burying).
        if (isLogo) {
          o.material.polygonOffset = true;
          o.material.polygonOffsetFactor = -4;
          o.material.polygonOffsetUnits = -4;
        }
        o.material.transparent = opacity < 1;
        o.material.opacity = opacity;
        o.material.depthWrite = opacity >= 1;
        o.material.needsUpdate = true;
      }
    });
  }, [cloned, colorOverride, metalnessOverride, roughnessOverride, opacity, part.color, part.metalness, part.roughness]);
  return <primitive object={cloned} />;
}

export function Part3D(props) {
  const { opacity, ...rest } = props;
  if (props.part?.primitive) return <PrimitivePart {...rest} opacity={opacity} />;
  return <GltfPart {...rest} opacity={opacity} />;
}
